import { useState } from 'react';
import CareerSectionTitle from '../shared/CareerSectionTitle';
import { Box } from '@mui/material';
import { useEmployeeService } from 'domain/shared/employee';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { FormShowTemplate } from 'framework/form';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import MentorshipContent from './MentorshipContent';
import { useShowContext } from 'framework/show';
import ConfirmationUpdateMentorModal, { PendingOperation } from './ConfirmationUpdateMentorModal';

const MentorshipSection = ({
  canHaveMenteesAsPolishContractor = false,
}: {
  canHaveMenteesAsPolishContractor?: boolean;
}) => {
  const { updateMentorshipInfo, getForUpdate, mapMentorshipInformationAfterUpdate } = useEmployeeService();
  const employeeId = useEmployeeId();
  const { data: employeeBeforeUpdate } = useShowContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingOperation, setPendingOperation] = useState<PendingOperation | null>(null);

  if (!employeeId) {
    return null;
  }

  const performUpdate = async (values: FormFieldsData): Promise<void | object> => {
    const updatedMentorshipInfo = await updateMentorshipInfo(employeeId, values);
    return mapMentorshipInformationAfterUpdate(employeeBeforeUpdate, updatedMentorshipInfo);
  };

  const handleSubmit = async (values: FormFieldsData): Promise<void | object> => {
    if (canHaveMenteesAsPolishContractor) {
      return performUpdate(values);
    }

    const previousMentor = employeeBeforeUpdate?.mentorshipInformation?.mentor;
    const currentMentorshipInfo = values['mentorshipInformation'];
    const currentMentor = currentMentorshipInfo?.mentor;

    // if we set mentor for the first time
    if (!previousMentor) {
      return performUpdate(values);
    }

    // mentor didn't change
    if (currentMentor === previousMentor) {
      return performUpdate(values);
    }

    // mentor changed - show confirmation modal
    return new Promise((resolve, reject) => {
      setPendingOperation({ values, resolve, reject });
      setIsModalOpen(true);
    });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setPendingOperation(null);
  };

  return (
    <Box minHeight="105px" position="relative">
      <CareerSectionTitle title="Mentorship" />

      <FormShowTemplate
        getDataFunc={async () => getForUpdate(employeeId)}
        submitFormFunc={handleSubmit}
        afterSubmit={{
          message: 'Worker information has been successfully updated.',
        }}
        isSmallLoader
      >
        <MentorshipContent canHaveMenteesAsPolishContractor={canHaveMenteesAsPolishContractor} />
      </FormShowTemplate>

      <ConfirmationUpdateMentorModal
        isOpen={isModalOpen}
        pendingOperation={pendingOperation}
        onConfirm={performUpdate}
        onClose={handleModalClose}
      />
    </Box>
  );
};

export default MentorshipSection;
